import { useTranslation } from 'react-i18next';
import {
  PositionStatusPill,
  SymbolPill,
  placesFor,
  triggeredLevels,
  type Position,
} from '../../entities/position';
import { cn } from '../../shared/lib/cn';
import { formatMoney, formatPercent, formatShares, toNumber } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { PlValue } from '../../shared/ui';
import { LadderTrack } from '../ladder-track';

export interface PositionCardProps {
  position: Position;
  selected: boolean;
  onSelect: () => void;
  onOpen: () => void;
}

export function PositionCard({ position, selected, onSelect, onOpen }: PositionCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const places = placesFor(position);
  const valuation = position.valuation;
  const next = position.next_level;
  const triggered = triggeredLevels(position).length;

  let distance: string;
  if (position.status === 'target_reached') distance = t('positionCard.atTarget');
  else if (triggered > 0) distance = t('positionCard.buyDue');
  else if (next && next.distance_pct !== null) {
    distance = t('positionCard.toLevel', {
      n: next.n,
      distance: formatPercent(Math.abs(toNumber(next.distance_pct)), locale),
    });
  } else if (valuation) {
    distance = t('positionCard.toTarget', {
      distance: formatPercent(valuation.to_target_pct, locale),
    });
  } else distance = '—';

  return (
    <article
      aria-label={t('positionCard.label', { symbol: position.instrument.display_symbol })}
      aria-current={selected || undefined}
      className={cn(
        'rounded-lg border border-border-subtle bg-bg-1 transition-colors duration-100',
        selected ? 'bg-bg-4' : 'hover:bg-bg-3',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        onDoubleClick={onOpen}
        className="block w-full rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        <div className="flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1.5">
          <SymbolPill symbol={position.instrument.display_symbol} />
          <span className="min-w-0 flex-1 truncate text-sm text-text-3 max-sm:hidden">
            {position.instrument.name}
          </span>
          <span className="flex-1 sm:hidden" />
          <span className="tabular text-xs text-text-3">
            {t('positionCard.invested', { amount: formatMoney(position.holding.cost, locale) })}
          </span>
          {valuation && (
            <PlValue value={toNumber(valuation.pl)} className="text-sm font-medium">
              {formatMoney(valuation.pl, locale, { signed: true })}
              <span className="ml-1 text-xs">
                {formatPercent(valuation.pl_pct, locale, { signed: true })}
              </span>
            </PlValue>
          )}
          <span className="tabular text-xs text-text-2">{distance}</span>
          <PositionStatusPill status={position.status} triggered={triggered} />
        </div>
        <LadderTrack position={position} />
        <p className="tabular px-4 pt-1 pb-2.5 text-xs text-text-3">
          {t('positionCard.summary', {
            shares: formatShares(position.holding.shares, locale),
            average: formatMoney(position.holding.average, locale, { places }),
            target: formatMoney(position.holding.target_price, locale, { places }),
            remaining: formatMoney(position.remaining_capital, locale),
          })}
        </p>
      </button>
    </article>
  );
}
